import StarIcon from '@components/Icon/Star';
import StarActiveIcon from '@components/Icon/StarActiveIcon';
import AvatarStatusFavorite from '@modules/talents/components/AvatarStatusFavorite';
import { FlexRow } from '@components/FlexRow';
import Colors from '@constants/Colors';
import formatDate from '@helper/formatDate';
import { Box, Card, CardActions, Rating, Stack, styled, Typography } from '@mui/material';
import IconButton from '@mui/material/IconButton';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { NotesManager } from './components/NotesManager';

interface TalentCardProps {
    talent: any;
    cardMenuList: {
        id: number;
        title: string;
        icon: React.ElementType;
        type: string;
        style?: string;
        path?: string;
        actionTitle?: string;
        actionDescription?: string;
        confirmTitle?: string;
        cancelTitle?: string;
        action?: string;
        description?: string;
        actionType?: string;
    }[];
    handleDownload: (url: string, label: string) => void;
    updateRating: (id: string, rating: number) => void;
    updateStatus?: (id: string, status: string) => void;
    onSuggestJobOffer?: (talent: any) => void;
    selectable?: boolean;
    selected?: boolean;
    onSelectionChange?: (talentId: string, selected: boolean, status?: string) => void;
    isModal?: boolean;
}

const CardTopContent = styled(Box)(({ theme }) => ({
    width: '100%',
    flex: 1,
    borderTopRightRadius: '8px',
    borderTopLeftRadius: '8px',
    paddingTop: '8.5px',
    paddingBottom: '27px',

    '& .card-header': {
        paddingLeft: '15.53px',
        paddingRight: '15.53px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        width: '100%',
        paddingBottom: '18.5px',
    },

    '& .card-content': {
        paddingLeft: '15.53px',
        paddingRight: '15.53px',
        margin: '0 auto',
        width: '100%',
    },
}));

const TalentCard = ({
    talent,
    cardMenuList,
    selectable = false,
    selected = false,
    onSelectionChange,
    isModal = false,
    handleDownload,
    updateRating,
    updateStatus,
    onSuggestJobOffer,
}: TalentCardProps) => {
    const navigate = useNavigate();
    const [notesDialogOpen, setNotesDialogOpen] = useState(false);

    const handleCardClick = () => {
        if (selectable && onSelectionChange) {
            onSelectionChange(talent._id, !selected, talent.currentStatusUser);
        }
    };

    return (
        <>
        <Card
            onClick={selectable ? handleCardClick : undefined}
            sx={{
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
                width: isModal ? '100%' : 'auto',
                minWidth: isModal ? '280px' : '306px',
                maxWidth: isModal ? '306px' : 'none',
                cursor: selectable ? 'pointer' : 'default',
                border: selected ? '2px solid #4C3DD0' : '2px solid rgba(255, 255, 255, 0.1)',
                borderRadius: '8px',
                backgroundColor: selected ? 'rgba(60, 45, 225, 0.3)' : '#222426',
                position: 'relative',
                overflow: 'visible',
            }}>
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    flex: 1,
                    justifyContent: 'start',
                    alignItems: 'start',
                }}>
                <CardTopContent>
                    <Box>
                        <Box className="card-header">
                            <Typography
                                typography={theme => theme.typography.fontFamily}
                                fontSize={12}
                                fontWeight={600}
                                sx={{
                                    color: theme => theme.palette.neutralGray.main,
                                }}>
                                Créé le{' '}
                                <Box
                                    component="span"
                                    sx={{
                                        color: theme => theme.palette.text.secondary,
                                    }}>
                                    {formatDate(talent.createdAt, 'DD.MM.YY (HH:mm)')}
                                </Box>
                            </Typography>
                            <Box sx={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                                {talent?.files[0]?.url && (
                                    <IconButton
                                        color="primary"
                                        sx={{
                                            width: '32px',
                                            height: '32px'
                                        }}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleDownload(talent?.files[0]?.url, `test_CV.pdf`);
                                        }}>
                                        <i className="fi fi-br-file-pdf" style={{
                                            fontSize: '16px',
                                        }}></i>
                                    </IconButton>
                                )}
                                <IconButton
                                    sx={{ backgroundColor: Colors.PRIMARY_PURPLE, width: '32px', height: '32px' }}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        if (onSuggestJobOffer) {
                                            onSuggestJobOffer(talent);
                                        }
                                    }}>
                                    <i className="fi fi-br-briefcase" style={{ color: '#fff', fontSize: '16px', marginTop: 2 }}></i>
                                </IconButton>
                            </Box>
                        </Box>
                        <Box className="card-content">
                            <Box
                                sx={{
                                    display: 'flex',
                                    gap: '18px',
                                    alignItems: 'flex-start',
                                }}>
                                <AvatarStatusFavorite
                                    talentIds={talent._id}
                                    image={talent.profilePicture?.url || ''}
                                    currentStatus={talent.currentStatusUser}
                                    upercutFavorites={talent.upercutFavorites}
                                    onStatusChange={(newStatus) => {
                                        if (updateStatus) {
                                            const backendStatus = newStatus === 'nothing' ? '' : newStatus;
                                            updateStatus(talent._id, backendStatus);
                                        }
                                    }}
                                    shouldStopPropagation
                                />
                                <Stack gap={1} style={{ width: '100%' }}>
                                    <Typography
                                        onClick={e => {
                                            if (!selectable) {
                                                e.stopPropagation();
                                                navigate(`/talents/detail/${talent._id}`, {
                                                    state: {
                                                        data: talent,
                                                    },
                                                });
                                            }
                                        }}
                                        variant="h5"
                                        fontSize="20px"
                                        fontWeight={700}
                                        lineHeight="100%"
                                        letterSpacing="0%"
                                        sx={{
                                            color: theme => theme.palette.text.primary,
                                            display: '-webkit-box',
                                            WebkitBoxOrient: 'vertical',
                                            WebkitLineClamp: 1,
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            cursor: selectable ? 'pointer' : 'pointer',
                                        }}>
                                        {talent.fullName || '-'}
                                    </Typography>
                                    <Box
                                        sx={{
                                            display: 'grid',
                                            gridTemplateColumns: '90px 1fr',
                                            position: 'relative',
                                            width: '100%',
                                            gap: 1,
                                            color: theme => theme.palette.text.secondary,
                                        }}>
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 1,
                                                minWidth: 0,
                                            }}>
                                            <i className="fi fi-br-briefcase" />
                                            <Typography
                                                variant="body2"
                                                sx={{
                                                    fontFamily: theme => theme.typography.fontFamilyBrand,
                                                    fontSize: '12px',
                                                    fontWeight: 500,
                                                    lineHeight: 'normal',
                                                    wordBreak: 'break-word',
                                                    display: '-webkit-box',
                                                    WebkitBoxOrient: 'vertical',
                                                    WebkitLineClamp: 1,
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                }}>
                                                {Math.round(talent.yearsOfExperience).toString() || '-'} an(s)
                                            </Typography>
                                        </Box>
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 1,
                                                minWidth: 0,
                                            }}>
                                            <i className="fi fi-br-marker" />
                                            <Typography
                                                variant="body2"
                                                sx={{
                                                    fontFamily: theme => theme.typography.fontFamilyBrand,
                                                    fontSize: '12px',
                                                    fontWeight: 500,
                                                    lineHeight: 'normal',
                                                    wordBreak: 'break-word',
                                                    display: '-webkit-box',
                                                    WebkitBoxOrient: 'vertical',
                                                    WebkitLineClamp: 1,
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                }}>
                                                {talent.city || '-'}
                                            </Typography>
                                        </Box>
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 1,
                                                minWidth: 0,
                                            }}>
                                            <i className="fi fi-br-diploma" />
                                            <Typography
                                                variant="body2"
                                                sx={{
                                                    fontFamily: theme => theme.typography.fontFamilyBrand,
                                                    fontSize: '12px',
                                                    fontWeight: 500,
                                                    lineHeight: 'normal',
                                                    wordBreak: 'break-word',
                                                    display: '-webkit-box',
                                                    WebkitBoxOrient: 'vertical',
                                                    WebkitLineClamp: 1,
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                }}>
                                                {talent.diplomas
                                                    ?.filter(diploma => !!diploma.type)
                                                    .map(diploma => diploma.type)
                                                    .join(', ') || '-'}
                                            </Typography>
                                        </Box>
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 1,
                                                minWidth: 0,
                                            }}>
                                            <i className="fi fi-br-diploma" />
                                            <Typography
                                                variant="body2"
                                                sx={{
                                                    fontFamily: theme => theme.typography.fontFamilyBrand,
                                                    fontSize: '12px',
                                                    fontWeight: 500,
                                                    lineHeight: 'normal',
                                                    wordBreak: 'break-word',
                                                    display: '-webkit-box',
                                                    WebkitBoxOrient: 'vertical',
                                                    WebkitLineClamp: 1,
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                }}>
                                                {talent.preferences?.join(', ') || '-'}
                                            </Typography>
                                        </Box>
                                    </Box>
                                </Stack>
                            </Box>
                        </Box>
                    </Box>
                </CardTopContent>
                <CardActions
                    sx={{
                        backgroundColor: 'rgba(60, 45, 225, 0.3)',
                        color: theme => theme.palette.primary['contrastText'],
                        width: '100%',
                        display: 'flex',
                        justifyContent: 'space-between',
                        padding: '0.5rem 1rem',
                        '& .MuiTypography-root': {
                            fontWeight: 600,
                        },
                        px: '12px',
                        alignItems: 'center',
                    }}>
                    <FlexRow gap="2px" alignItems="center" sx={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '4px',
                                cursor: 'pointer',
                                '&:hover': {
                                    opacity: 0.8,
                                }
                            }}
                            onClick={(e) => {
                                e.stopPropagation();
                                setNotesDialogOpen(true);
                            }}>
                            <Box
                                sx={{
                                    fontWeight: 700,
                                    fontSize: '14px',
                                    color: theme => theme.palette.neutralGray.main,
                                }}>
                                {talent?.upercutNotes?.length || 0}
                            </Box>
                        </Box>
                        <Box
                            sx={{
                                padding: '0px 0px 0px 8px',
                                borderRadius: '8px',
                                background: theme => theme.palette.primaryPurple.main,
                            }}>
                            <Rating
                                value={talent?.upercutAverageRating || 0}
                                sx={{ color: '#ffd700' }}
                                icon={<StarActiveIcon style={{ marginRight: '8px' }} />}
                                emptyIcon={<StarIcon style={{ marginRight: '8px' }} />}
                                onChange={(event, newValue) => {
                                    updateRating(talent._id, newValue || 0);
                                }}
                            />
                        </Box>
                    </FlexRow>
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px',
                            fontSize: '12px',
                            fontWeight: 600,
                            color: theme => theme.palette.neutralGray.main,
                        }}>
                        <Box sx={{ fontWeight: 600, color: theme => theme.palette.neutralLightGrey.main }}>
                            {(talent?.jobApplications && talent.jobApplications?.length) || 0} {`Offre${(talent?.jobApplications && talent.jobApplications?.length) > 1 ? 's' : ''} d'emploi postulée${(talent?.jobApplications && talent.jobApplications?.length) > 1 ? 's' : ''}`}
                        </Box>
                    </Box>
                </CardActions>
            </Box>
        </Card>
        <NotesManager
            open={notesDialogOpen}
            onClose={() => setNotesDialogOpen(false)}
            talentId={talent._id}
            talentName={talent.fullName}
        />
    </>
    );
};

export default TalentCard;
